/**
 * UNCOVERED COLOR -- a product color that NO placement decorates (Jake,
 * 2026-10-22; definitions provisional pending his confirmation, so the whole
 * predicate lives HERE and in its backend twin, nowhere else).
 *
 * Orders for an uncovered color go out as blank goods. The rule:
 *
 *   * a placement APPLIES to a color when the route the color resolves to
 *     exists and is not an empty shade (utils/emptyShade.js);
 *   * a color is COVERED when at least one placement applies to it, so one
 *     placement that decorates it is enough;
 *   * a color no placement applies to is UNCOVERED. This subsumes the #4613
 *     empty-shade case for the colors that route to an empty shade;
 *   * a product with ZERO placements is excluded (an empty product is the
 *     stub-discard prompt's business), as is a product with no colors.
 *
 * The backend twin is ``uncovered_color_skus`` in
 * api/src/services/variant_resolver.py, which feeds the product list/card
 * icon. Keep the two identical. Both reuse their side's variant resolver
 * rather than re-deriving routing.
 */
import { resolveVariant } from "@/composables/useVariantResolver";
import { isEmptyShade } from "@/utils/emptyShade";

/**
 * @param {Array} decorations    the editor's placements (each with `variants`)
 * @param {Array} productColors  [{color_sku, name, hex}] the product offers
 * @param {{manualLd?: object|null, colorOverrides?: object|null}} [ctx]
 * @returns {Array} the uncovered entries of `productColors`, in input order
 */
export function uncoveredColorsOf(decorations, productColors, ctx = {}) {
    const placements = Array.isArray(decorations) ? decorations : [];
    const colors = Array.isArray(productColors) ? productColors : [];
    if (placements.length === 0 || colors.length === 0) return [];
    const manualLd = ctx?.manualLd ?? null;
    const colorOverrides = ctx?.colorOverrides ?? null;

    return colors.filter((color) => {
        if (!color?.color_sku) return false;
        const covered = placements.some((placement) => {
            const routed = resolveVariant(
                placement, color, manualLd, colorOverrides,
            );
            if (!routed) return false;
            return !isEmptyShade(routed, placement.variants);
        });
        return !covered;
    });
}

/**
 * The product's selected colors as {color_sku, name, hex}, hex/name from the
 * supplier's color list when it has one. A supplier with no catalog
 * integration publishes no list at all; its colors keep their SKU as name
 * and the light/dark classifier falls back to the name.
 *
 * @param {string[]} selectedSkus
 * @param {Array|null|undefined} supplierColors  supplierMeta.colors
 */
export function selectedProductColors(selectedSkus, supplierColors) {
    const bySku = new Map(
        (Array.isArray(supplierColors) ? supplierColors : []).map((c) => [c.color_sku, c]),
    );
    return (Array.isArray(selectedSkus) ? selectedSkus : []).map((sku) => {
        const row = bySku.get(sku);
        return row
            ? { color_sku: sku, name: row.name ?? sku, hex: row.hex ?? null }
            : { color_sku: sku, name: sku, hex: null };
    });
}
